import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { Connection } from '@modules/connections/types/connection';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { type Message } from '@modules/messages/types/message';
import { UserAvatarModule } from '@modules/user-avatar/user-avatar.module';
import { ContactsInlineListComponent } from '../contacts-inline-list/contacts-inline-list.component';
import { ParticipantContextMenuDirective } from '../../directives/participant-context-menu.directive';
import { FormatParticipantPipe } from '../../pipes/format-participant.pipe';
import { type Participant } from '../../types/participant';
import { merge, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, tap, shareReplay } from 'rxjs/operators';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { ContactSummaryDto } from '@sharedlib/dtos/dto/contacts-app/contact.dto';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';

@Component({
  selector: 'app-message-connections',
  templateUrl: './message-connections.component.html',
  styleUrls: ['./message-connections.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UserAvatarModule,
    MatIconModule,
    ContactsInlineListComponent,
    ParticipantContextMenuDirective,
    FormatParticipantPipe,
  ],
})
export class MessageConnectionsComponent {
  // Inputs
  public readonly message = input.required<Message>();

  // Services
  private readonly kpService = inject(KnowledgePanelService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contactsService = inject(ContactsService);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly dpService = inject(DynamicPanelService);
  private readonly formChangesModalsService = inject(FormChangesModalsService);

  // State
  protected readonly selectedConnections = signal<Connection[]>([]);

  // Signals
  private readonly contactsByEmail = signal<ContactSummaryDto[]>([]);
  public readonly fromParticipant = computed<Participant>(() => {
    const contacts = this.contactsByEmail();

    if (contacts.length) {
      const from = this.message().from;

      return {
        ...from,
        contactIds: contacts.map((contact) => contact.id),
        // SBUX-824: contacts-app is the single source of the VIP flag, so the crown reads it from
        // there and nowhere else — same source the VIP folder filters on.
        vip: contacts.some((contact) => contact.vip),
      };
    }

    return this.message().from;
  });

  constructor() {
    // Subscribe to selected connections
    this.kpService
      .getSelectedConnections()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((connections: Connection[]) => {
        this.selectedConnections.set(connections);
      });

    // Combine initial fetch and refetch on contact creation
    const message$ = toObservable(this.message).pipe(
      tap(() => this.contactsByEmail.set([])),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const initialFetch$ = message$.pipe(
      switchMap((msg) =>
        !msg.accountId || !msg.from?.address ? of([]) : this.contactsService.getContactsByEmails([msg.from.address], { tenantItems: [msg] }).pipe(
          catchError((error) => {
            console.error('Failed to fetch contacts by email:', msg.from.address, error);
            return of([]);
          }),
        ),
      ),
    );

    // When we've clicked "Add to Contacts" menu item, and contact was created,
    // we receive a web-socket signal from the backend,
    // we need to refetch the contacts by email to update the context menu, with "Open Contact" or "View Contacts"
    // menu items instead
    const refetchOnContactCreated$ = message$.pipe(
      switchMap((msg) =>
        !msg.accountId || !msg.from?.address ? of([]) : this.stitchEventsService
          .getEvents(msg.accountId, StitchType.CONTACT, [SignalStitchEventType.CREATED])
          .pipe(
            // filter out contacts
            map((stitchEvent) => stitchEvent.items),
            // filter out only matched by Email contacts
            map((contacts) =>
              contacts.filter((contact) => contact?.emails?.some((email) => email?.value === msg.from.address)),
            ),
            // construct new contacts for the optimistic UI update,
            // or we can re-fetch, decided to do UI update only, to reduce the number of requests
            map((matchedNewContacts) => [...this.contactsByEmail(), ...matchedNewContacts]),
          ),
      ),
    );

    // When we've restored deleted contact from the Trash
    // we receive a web-socket signal from the backend,
    // we need to refetch the contacts by email to update the context menu, with "Open Contact" or "View Contacts"
    // menu items instead
    const refetchOnContactRestoredFromTrash$ = message$.pipe(
      switchMap((msg) =>
        !msg.accountId || !msg.from?.address ? of([]) : this.stitchEventsService
          .getEvents(msg.accountId, StitchType.CONTACT, [
            SignalStitchEventType.RESTORED_FROM_TRASH,
            // when current From: contact was opened in DP
            // Email was changed, we need to re-fetch as well
            // As the menu now should switch:
            // - From - "View Contacts" to "Open Contact"
            // OR
            // - From - "Open Contact" to "Add to Contacts"
            SignalStitchEventType.UPDATED,
          ])
          .pipe(
            // as we don't have items in the signal, just ids, we can only re-fetch here
            switchMap(() => {
              const address = msg.from?.address;
              if (!address) {
                return of<ContactSummaryDto[]>([]);
              }
              return this.contactsService.getContactsByEmails([address], { tenantItems: [msg] }).pipe(
                catchError((error) => {
                  console.error('Failed to refetch contacts by email:', address, error);
                  return of([]);
                }),
              );
            }),
          ),
      ),
    );

    // When we've moved contact to the Trash,
    // we receive a web-socket signal from the backend,
    // if id of such moved to the trash contact matches one of known contacts, we need to re-fetch contacts by email
    // to update the menu item with "Add to Contacts" or "Open Contact"
    const refetchOnContactMovedToTrash$ = message$.pipe(
      switchMap((msg) =>
        !msg.accountId || !msg.from?.address ? of([]) : this.stitchEventsService
          .getEvents(msg.accountId, StitchType.CONTACT, [SignalStitchEventType.MOVED_TO_TRASH])
          .pipe(
            map((stitchEvent) => stitchEvent.ids),
            // calculate contacts to remove
            map((movedToTrashContactIds) =>
              this.contactsByEmail()
                .filter((existingContact) => movedToTrashContactIds.includes(existingContact.id))
                .map((contactToRemove) => contactToRemove.id),
            ),
            // filter out empty contact Ids
            filter((contactToRemoveIds) => !!contactToRemoveIds.length),
            // construct new contacts for the optimistic UI update,
            // or we can re-fetch, decided to do UI update only, to reduce the number of requests
            map((contactToRemoveIds) =>
              this.contactsByEmail().filter((existingContact) => !contactToRemoveIds.includes(existingContact.id)),
            ),
          ),
      ),
    );

    merge(initialFetch$, refetchOnContactCreated$, refetchOnContactMovedToTrash$, refetchOnContactRestoredFromTrash$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contactSummaryDTOs) => {
        this.contactsByEmail.set(contactSummaryDTOs);
      });
  }

  /**
   * Actions
   */

  protected openSenderContactInDp(): void {
    const nonAutogenerated = this.contactsByEmail().filter((c) => !c.autogenerated);
    if (nonAutogenerated.length !== 1) {
      return;
    }

    this.formChangesModalsService
      .confirmUnsavedChanges()
      .pipe(first())
      .subscribe((canLeave) => {
        if (canLeave) {
          this.contactsService
            .getItem(nonAutogenerated[0])
            .pipe(take(1))
            .subscribe((item) => {
              this.dpService.setFormItem(item);
            });
        }
      });
  }
}
